/**
 * Default NetScheduler for real sessions.
 *
 * Browsers throttle main-thread timers in hidden tabs (≥ 1 s, and far worse after a few minutes), which would
 * starve the packet pump and get us disconnected while the player alt-tabs. A dedicated Worker's timers are not
 * throttled that way, so in browsers the intervals run inside a tiny inline Worker that posts a message per
 * tick; the callback runs on the main thread. Falls back to `setInterval` when Workers or blob URLs are
 * unavailable (Node, strict CSP) — including when the Worker fails to load asynchronously (its `error` event),
 * in which case every running interval is moved to `setInterval` on the spot. Guarded feature detection only:
 * safe to import from Node tests.
 */
import type { NetScheduler } from './types';

const WORKER_SOURCE = `
const timers = new Map();
onmessage = (e) => {
  const [op, id, ms] = e.data;
  if (op === 1) timers.set(id, setInterval(() => postMessage(id), ms));
  else { clearInterval(timers.get(id)); timers.delete(id); }
};`;

interface WorkerTimer {
  every(ms: number, fn: () => void): () => void;
}

interface Entry {
  ms: number;
  fn: () => void;
  /** setInterval handle once running on the fallback. */
  fallback: ReturnType<typeof setInterval> | null;
}

let sharedWorkerTimer: WorkerTimer | null | undefined;

const createWorkerTimer = (): WorkerTimer | null => {
  const g = globalThis as {
    Worker?: typeof Worker;
    Blob?: typeof Blob;
    URL?: typeof URL;
    document?: unknown;
  };
  if (!g.Worker || !g.Blob || !g.URL?.createObjectURL || g.document === undefined) return null;
  try {
    const url = g.URL.createObjectURL(new g.Blob([WORKER_SOURCE], { type: 'text/javascript' }));
    const worker = new g.Worker(url);
    const entries = new Map<number, Entry>();
    let failed = false;
    let nextId = 1;
    worker.onmessage = (e: MessageEvent<number>) => entries.get(e.data)?.fn();
    worker.onerror = () => {
      if (failed) return;
      failed = true;
      for (const entry of entries.values()) {
        if (!entry.fallback) entry.fallback = setInterval(entry.fn, entry.ms);
      }
      try {
        worker.terminate();
      } catch {
        // already gone
      }
    };
    return {
      every(ms, fn) {
        const id = nextId++;
        const entry: Entry = { ms, fn, fallback: null };
        entries.set(id, entry);
        if (failed) entry.fallback = setInterval(fn, ms);
        else worker.postMessage([1, id, ms]);
        return () => {
          if (!entries.delete(id)) return;
          if (entry.fallback) clearInterval(entry.fallback);
          else if (!failed) worker.postMessage([0, id, 0]);
        };
      },
    };
  } catch {
    return null;
  }
};

const now: () => number =
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? () => performance.now()
    : () => Date.now();

export const createDefaultScheduler = (): NetScheduler => {
  if (sharedWorkerTimer === undefined) sharedWorkerTimer = createWorkerTimer();
  const wt = sharedWorkerTimer;
  return {
    now,
    every(ms, fn) {
      if (wt) return wt.every(ms, fn);
      const id = setInterval(fn, ms);
      return () => clearInterval(id);
    },
  };
};
